import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const REFRESH_INTERVAL_MS = 10000; // auto-refresh every 10s

export default function SystemHealthPage() {
  const [data, setData]       = useState(null);
  const [cycles, setCycles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg]         = useState(null);
  const [tab, setTab]         = useState("overview"); // overview | cycles
  const intervalRef           = useRef(null);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get("/admin/system/health");
      setData(res.data);
    } catch {
      // silently fail on auto-refresh
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCycles = useCallback(async () => {
    try {
      const res = await axios.get("/admin/system/cycles");
      setCycles(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchHealth();
    fetchCycles();
    intervalRef.current = setInterval(() => {
      fetchHealth();
      if (tab === "cycles") fetchCycles();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchHealth, fetchCycles, tab]);

  const triggerCycle = async (force) => {
    setRunning(true);
    try {
      await axios.post("/admin/system/cycle/run", { force });
      flash(force ? "Forced cycle triggered." : "Cycle triggered (market hours apply).");
      setTimeout(() => { fetchHealth(); fetchCycles(); }, 2000);
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to trigger cycle.", false);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div style={{ padding: 30, color: "#888" }}>Loading…</div>;
  if (!data)   return <div style={{ padding: 30, color: "#dc3545" }}>Failed to load health data.</div>;

  const { system, scheduler, cycles: cycleData } = data;
  const stats = cycleData?.stats || {};

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={pageTitle}>System Health</h2>
          <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
            Auto-refreshes every 10s &nbsp;·&nbsp;
            <span style={{ color: scheduler.market_open ? "#28a745" : "#888" }}>
              Market {scheduler.market_open ? "OPEN" : "CLOSED"}
            </span>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { fetchHealth(); fetchCycles(); }} style={btnSecondary}>Refresh</button>
          <button onClick={() => triggerCycle(false)} disabled={running} style={btnSecondary}>
            Run Cycle
          </button>
          <button onClick={() => triggerCycle(true)} disabled={running} style={{ ...btnSecondary, color: "#dc3545", borderColor: "#dc3545" }}>
            Force Run
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={tabBar}>
        {[["overview", "Overview"], ["cycles", "Cycle History"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...tabBtn, ...(tab === key ? tabBtnActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          {/* ── System Resources ── */}
          <SectionTitle>System Resources</SectionTitle>
          {system?.available ? (
            <div style={resourceGrid}>
              <GaugeCard
                label="CPU Usage"
                value={system.cpu_percent}
                unit="%"
                color={system.cpu_percent > 80 ? "#dc3545" : system.cpu_percent > 60 ? "#fd7e14" : "#28a745"}
              />
              <BarCard
                label="Memory"
                used={system.memory.used_gb}
                total={system.memory.total_gb}
                pct={system.memory.percent}
                unit="GB"
              />
              <BarCard
                label="Disk"
                used={system.disk.used_gb}
                total={system.disk.total_gb}
                pct={system.disk.percent}
                unit="GB"
              />
            </div>
          ) : (
            <div style={infoBox}>psutil not available — install it in the backend container to see system metrics.</div>
          )}

          {/* ── Scheduler Status ── */}
          <SectionTitle>Scheduler Status</SectionTitle>
          <div style={card}>
            <div style={schedGrid}>
              <SchedRow label="Status"
                value={scheduler.running
                  ? <span style={{ color: "#007bff", fontWeight: 700 }}>● RUNNING</span>
                  : <span style={{ color: "#28a745", fontWeight: 700 }}>● IDLE</span>}
              />
              <SchedRow label="Last Run"          value={scheduler.last_run_at || "—"} />
              <SchedRow label="Last Result"       value={<ResultBadge result={scheduler.last_result} />} />
              <SchedRow label="Next Run In"       value={scheduler.next_run_in_s != null ? `~${fmtSecs(scheduler.next_run_in_s)}` : "—"} />
              <SchedRow label="Cycle Interval"    value={fmtSecs(scheduler.cycle_interval_s)} />
              <SchedRow label="Thread Pool Size"  value={`${scheduler.max_workers} workers`} />
              <SchedRow label="Scheduler Uptime"  value={scheduler.uptime_s != null ? fmtSecs(scheduler.uptime_s) : "—"} />
              {scheduler.last_error && (
                <SchedRow label="Last Error"
                  value={<span style={{ color: "#dc3545", fontSize: 12 }}>{scheduler.last_error}</span>}
                />
              )}
            </div>
          </div>

          {/* ── Scaling Indicator ── */}
          <SectionTitle>Scaling Indicator</SectionTitle>
          <ScalingPanel stats={stats} interval={scheduler.cycle_interval_s} maxWorkers={scheduler.max_workers} />

          {/* ── Last Cycle Breakdown ── */}
          {(cycleData?.recent || []).length > 0 && !cycleData.recent[0].skipped && (
            <>
              <SectionTitle>Last Cycle — Per-Account Breakdown</SectionTitle>
              <AccountBreakdown cycle={cycleData.recent[0]} />
            </>
          )}
        </>
      )}

      {tab === "cycles" && (
        <>
          <SectionTitle>Cycle History (last {cycles.length})</SectionTitle>
          <CycleHistoryTable cycles={cycles} />
        </>
      )}
    </div>
  );
}

/* ── Scaling Panel ────────────────────────────────────────────── */

function ScalingPanel({ stats, interval, maxWorkers }) {
  const pct    = stats.window_utilization_pct;
  const phase  = stats.phase2_recommendation;
  const colors = { green: "#28a745", yellow: "#fd7e14", red: "#dc3545" };
  const labels = {
    green:  "All good — plenty of headroom",
    yellow: "Monitor closely — plan Phase 2 (Celery)",
    red:    "Critical — migrate to Phase 2 immediately",
    "Insufficient data": "Not enough cycles yet to evaluate",
    "No data yet":       "No cycles run yet",
  };

  const color  = colors[phase] || "#6c757d";
  const label  = labels[phase] || phase;

  return (
    <div style={{ ...card, padding: "20px 24px" }}>
      <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
        {/* Traffic light */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, minWidth: 100 }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: color, boxShadow: `0 0 20px ${color}55`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28,
          }}>
            {phase === "green" ? "✓" : phase === "yellow" ? "!" : phase === "red" ? "⚠" : "?"}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase" }}>
            {phase === "green" ? "Healthy" : phase === "yellow" ? "Watch" : phase === "red" ? "Scale Now" : "Pending"}
          </div>
        </div>

        {/* Metrics */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: "#333", marginBottom: 12 }}>{label}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <MiniStat label="Window Utilization"     value={pct != null ? `${pct}%` : "—"} color={color} />
            <MiniStat label="Avg Cycle Duration"     value={stats.avg_duration_s != null ? `${stats.avg_duration_s}s` : "—"} />
            <MiniStat label="Worst Cycle"            value={stats.max_duration_s != null ? `${stats.max_duration_s}s` : "—"} />
            <MiniStat label="Avg Accounts / Cycle"   value={stats.avg_accounts != null ? stats.avg_accounts : "—"} />
            <MiniStat label="Avg Time / Account"     value={stats.avg_per_account_s != null ? `${stats.avg_per_account_s}s` : "—"} />
            <MiniStat label="Est. Capacity (Phase 1)"
              value={stats.estimated_capacity_accounts != null ? `~${stats.estimated_capacity_accounts} accounts` : "—"}
              color="#007bff"
            />
          </div>
          {pct != null && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                Window utilization ({pct}% of {interval}s cycle)
              </div>
              <div style={{ height: 10, background: "#f0f0f0", borderRadius: 5, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${Math.min(pct, 100)}%`,
                  background: color, borderRadius: 5, transition: "width 0.5s",
                }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Account Breakdown ────────────────────────────────────────── */

function AccountBreakdown({ cycle }) {
  const accounts = cycle?.accounts || [];
  if (!accounts.length) return <div style={infoBox}>No accounts processed.</div>;

  const steps = ["broker_sync", "snapshot", "rules", "actions", "orders"];

  return (
    <div style={{ ...card, overflowX: "auto" }}>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>Account</th>
            <th style={th}>Total</th>
            {steps.map(s => <th key={s} style={th}>{stepLabel(s)}</th>)}
            <th style={th}>Actions</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map(a => (
            <tr key={a.username}>
              <td style={td}><strong>{a.username}</strong></td>
              <td style={{ ...td, fontWeight: 700 }}>{a.duration_s}s</td>
              {steps.map(s => (
                <td key={s} style={{ ...td, fontFamily: "monospace", fontSize: 12, color: "#555" }}>
                  {a.steps?.[s] != null ? `${a.steps[s]}s` : "—"}
                </td>
              ))}
              <td style={td}>{a.actions_fired ?? "—"}</td>
              <td style={td}>
                {a.ok
                  ? <span style={{ color: "#28a745", fontWeight: 700 }}>OK</span>
                  : <span style={{ color: "#dc3545", fontSize: 12 }} title={a.error}>ERROR</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Cycle History Table ──────────────────────────────────────── */

function CycleHistoryTable({ cycles }) {
  const [expanded, setExpanded] = useState(null);

  if (!cycles.length) return <div style={infoBox}>No cycles recorded yet.</div>;

  return (
    <div style={{ ...card, overflowX: "auto" }}>
      <table style={table}>
        <thead>
          <tr>
            {["Started", "Duration", "Accounts", "OK", "Errors", "Status", ""].map(h => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cycles.map((c, i) => (
            <React.Fragment key={i}>
              <tr style={{ background: expanded === i ? "#f0f7ff" : "#fff" }}>
                <td style={{ ...td, fontSize: 12, fontFamily: "monospace" }}>{c.started_at}</td>
                <td style={{ ...td, fontWeight: 700 }}>
                  {c.skipped ? "—" : c.duration_s != null ? `${c.duration_s}s` : "—"}
                </td>
                <td style={td}>{c.skipped ? "—" : c.total_accounts}</td>
                <td style={{ ...td, color: "#28a745", fontWeight: 700 }}>{c.skipped ? "—" : c.ok_count}</td>
                <td style={{ ...td, color: c.error_count > 0 ? "#dc3545" : "#888", fontWeight: c.error_count > 0 ? 700 : 400 }}>
                  {c.skipped ? "—" : c.error_count}
                </td>
                <td style={td}>
                  {c.skipped
                    ? <span style={{ color: "#888", fontSize: 11 }}>SKIPPED</span>
                    : <ResultBadge result={c.error_count > 0 ? "error" : "ok"} />
                  }
                </td>
                <td style={td}>
                  {!c.skipped && c.accounts?.length > 0 && (
                    <button onClick={() => setExpanded(expanded === i ? null : i)} style={btnTiny}>
                      {expanded === i ? "▲" : "▼"} Details
                    </button>
                  )}
                </td>
              </tr>
              {expanded === i && (
                <tr>
                  <td colSpan={7} style={{ padding: "0 16px 16px 32px", background: "#f0f7ff" }}>
                    <AccountBreakdown cycle={c} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Small sub-components ─────────────────────────────────────── */

function SectionTitle({ children }) {
  return <div style={sectionTitle}>{children}</div>;
}

function GaugeCard({ label, value, unit, color }) {
  return (
    <div style={resourceCard}>
      <div style={{ fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>{value}<span style={{ fontSize: 16, marginLeft: 2 }}>{unit}</span></div>
      <div style={{ marginTop: 10, height: 6, background: "#f0f0f0", borderRadius: 3 }}>
        <div style={{ height: "100%", width: `${Math.min(value, 100)}%`, background: color, borderRadius: 3 }} />
      </div>
    </div>
  );
}

function BarCard({ label, used, total, pct, unit }) {
  const color = pct > 85 ? "#dc3545" : pct > 70 ? "#fd7e14" : "#28a745";
  return (
    <div style={resourceCard}>
      <div style={{ fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#2c3e50", lineHeight: 1 }}>
        {used}<span style={{ fontSize: 13, color: "#888", marginLeft: 2 }}>{unit}</span>
      </div>
      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>of {total} {unit} ({pct}%)</div>
      <div style={{ marginTop: 10, height: 6, background: "#f0f0f0", borderRadius: 3 }}>
        <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 3 }} />
      </div>
    </div>
  );
}

function SchedRow({ label, value }) {
  return (
    <div style={schedRow}>
      <div style={schedLabel}>{label}</div>
      <div style={schedValue}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={miniStatBox}>
      <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color || "#2c3e50" }}>{value}</div>
    </div>
  );
}

function ResultBadge({ result }) {
  const meta = {
    ok:      { label: "OK",      color: "#28a745", bg: "#d4edda" },
    error:   { label: "ERROR",   color: "#dc3545", bg: "#fde2e2" },
    skipped: { label: "SKIPPED", color: "#6c757d", bg: "#e2e3e5" },
  };
  const m = meta[result] || { label: result || "—", color: "#888", bg: "#eee" };
  return (
    <span style={{ padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}

/* ── Helpers ──────────────────────────────────────────────────── */

function fmtSecs(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

function stepLabel(key) {
  return { broker_sync: "Broker", snapshot: "Snapshot", rules: "Rules", actions: "Actions", orders: "Orders" }[key] || key;
}

/* ── Styles ───────────────────────────────────────────────────── */

const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast        = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.8px", margin: "24px 0 10px" };
const card         = { background: "#fff", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", marginBottom: 16 };
const resourceGrid = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 0 };
const resourceCard = { background: "#fff", borderRadius: 12, padding: "18px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" };
const schedGrid    = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 };
const schedRow     = { display: "flex", justifyContent: "space-between", padding: "10px 20px", borderBottom: "1px solid #f5f5f5" };
const schedLabel   = { fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase" };
const schedValue   = { fontSize: 13, color: "#2c3e50", fontWeight: 500 };
const miniStatBox  = { background: "#f8f9fb", borderRadius: 8, padding: "10px 12px" };
const table        = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th           = { textAlign: "left", padding: "10px 14px", background: "#f8f9fa", borderBottom: "2px solid #dee2e6", fontSize: 11, fontWeight: 700, color: "#6c757d", textTransform: "uppercase" };
const td           = { padding: "9px 14px", borderBottom: "1px solid #f0f0f0", verticalAlign: "middle" };
const infoBox      = { background: "#f8f9fa", borderRadius: 8, padding: "14px 18px", color: "#888", fontSize: 13 };
const tabBar       = { display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid #dee2e6", paddingBottom: 0 };
const tabBtn       = { padding: "8px 18px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "#888", fontWeight: 500, borderBottom: "2px solid transparent", marginBottom: -2 };
const tabBtnActive = { color: "#007bff", borderBottomColor: "#007bff", fontWeight: 700 };
const btnSecondary = { padding: "7px 14px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 12 };
const btnTiny      = { padding: "2px 8px", borderRadius: 6, border: "1px solid #dee2e6", background: "#fff", cursor: "pointer", fontSize: 11 };
